import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Empty,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ExamSubjectRow, MarkEntryRow } from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin surface — Results & moderation.
 * Read-only viewer of marks per subject. Moderation writes-additively land in
 * a follow-up (A-04 preserved via audit log). "All-or-nothing" publishing
 * is on the parent exam via ExamDetail.
 */
export function ResultsModerationPage() {
  const { institution = '', code = '' } = useParams<{ institution: string; code: string }>();
  const navigate = useNavigate();
  const [selectedSubject, setSelectedSubject] = useState<string | undefined>();

  const exam = useQuery({
    queryKey: ['exam', institution, code],
    queryFn: () => productApi.getExam(institution, code),
    enabled: !!institution && !!code,
  });

  const chosen: ExamSubjectRow | undefined = selectedSubject
    ? exam.data?.subjects.find((s) => s.id === selectedSubject)
    : exam.data?.subjects[0];

  const sheet = useQuery({
    queryKey: ['marks-sheet', institution, chosen?.id],
    queryFn: () => productApi.getMarksSheet(institution, chosen!.id),
    enabled: !!institution && !!chosen?.id,
  });

  if (exam.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!exam.data) return <Alert type="error" showIcon message="Exam not found" />;

  const entries = sheet.data?.entries ?? [];
  const totalMax = chosen?.max_marks ?? 0;
  const passing = chosen?.passing_marks ?? 0;
  const passed = entries.filter((e) => !e.absent && e.marks_obtained !== null && e.marks_obtained >= passing).length;
  const failed = entries.filter((e) => !e.absent && e.marks_obtained !== null && e.marks_obtained < passing).length;
  const absent = entries.filter((e) => e.absent).length;
  const pending = entries.length - passed - failed - absent;
  const avg = entries.length > 0
    ? Math.round(
        (entries.filter((e) => e.marks_obtained !== null).reduce((a, e) => a + (e.marks_obtained ?? 0), 0)
          / Math.max(1, entries.filter((e) => e.marks_obtained !== null).length))
          * 10,
      ) / 10
    : 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space size={4}>
        <Button type="text" size="small" onClick={() => navigate(`/i/${institution}/admin/exams/${code}`)}>← Exam</Button>
        <Typography.Text type="secondary">/ Results</Typography.Text>
      </Space>

      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Results · {exam.data.exam.name}</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Per-subject viewer. Moderation writes-additively (A-04); every change lives in the audit log.
        </Typography.Text>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Select
          size="small"
          value={chosen?.id}
          onChange={setSelectedSubject}
          style={{ minWidth: 320 }}
          options={exam.data.subjects.map((s) => ({
            value: s.id,
            label: `${s.subject_name} · ${s.grade}${s.section ? `-${s.section}` : ''} · ${s.date}`,
          }))}
        />
      </div>

      {!chosen && (
        <Card size="small">
          <Empty description="Add subjects to this exam first." />
        </Card>
      )}

      {chosen && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 12 }}>
            <Card size="small"><Statistic title="Students" value={entries.length} /></Card>
            <Card size="small"><Statistic title="Passed" value={passed} valueStyle={{ color: '#1c6442' }} /></Card>
            <Card size="small"><Statistic title="Failed" value={failed} valueStyle={{ color: failed > 0 ? '#b3382f' : undefined }} /></Card>
            <Card size="small"><Statistic title="Absent" value={absent} /></Card>
            <Card size="small"><Statistic title="Avg / {max}" value={`${avg} / ${totalMax}`} /></Card>
          </div>

          <Card size="small">
            {sheet.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
            {entries.length === 0 && !sheet.isLoading && <Empty description="No entries yet." style={{ padding: 20 }} />}
            {entries.length > 0 && (
              <Table<MarkEntryRow>
                size="small"
                pagination={{ pageSize: 15 }}
                rowKey="student_id"
                dataSource={entries}
                columns={[
                  { title: 'Adm. no.', dataIndex: 'admission_number', key: 'a', width: 110, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
                  { title: 'Name', dataIndex: 'name', key: 'n' },
                  {
                    title: 'Marks',
                    key: 'm',
                    width: 100,
                    align: 'right',
                    render: (_, r) => r.absent ? <Typography.Text type="secondary">absent</Typography.Text> : r.marks_obtained ?? <Typography.Text type="secondary">—</Typography.Text>,
                  },
                  {
                    title: 'Max',
                    key: 'max',
                    width: 60,
                    align: 'right',
                    render: () => totalMax,
                  },
                  {
                    title: 'Result',
                    key: 'res',
                    width: 100,
                    render: (_, r) => {
                      if (r.absent) return <Tag>abs</Tag>;
                      if (r.marks_obtained == null) return <Tag>—</Tag>;
                      return r.marks_obtained >= passing ? <Tag color="success">pass</Tag> : <Tag color="error">fail</Tag>;
                    },
                  },
                  {
                    title: '',
                    key: 'reportcard',
                    width: 150,
                    align: 'right',
                    render: (_, r) => (
                      <Button size="small" onClick={() => navigate(`/i/${institution}/admin/reportcards/${code}?student=${r.admission_number}`)}>
                        Report card
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </>
      )}
    </Space>
  );
}
